import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { EXPERIENCES } from "../data/site";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HELIS = [
  { v: "", label: "Sans préférence" },
  { v: "R22", label: "Robinson R22" },
  { v: "R44", label: "Robinson R44" },
  { v: "AS350", label: "Eurocopter AS350" },
];

const DURATIONS = [
  { v: "18 min", label: "18 minutes" },
  { v: "30 min", label: "30 minutes" },
  { v: "42 min", label: "42 minutes" },
  { v: "60 min", label: "60 minutes" },
  { v: "Sur devis", label: "Sur devis / autre" },
];

export default function BookingForm({ compact = false }) {
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    flight_type: EXPERIENCES[0].title,
    helicopter: "",
    duration: "",
    passengers: 1,
    preferred_date: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const upd = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.phone) {
      toast.error("Merci de remplir vos coordonnées.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        passengers: Number(form.passengers) || 1,
      };
      await axios.post(`${API}/bookings`, payload);
      setDone(true);
      toast.success("Demande envoyée. Nous revenons vers vous.");
    } catch (err) {
      console.error(err);
      toast.error("Envoi impossible. Écrivez-nous à info@bscopter.be");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.2, 0.8, 0.2, 1] }}
        data-testid="booking-success"
        className="border border-black/10 bg-white p-10 md:p-14"
      >
        <div className="overline text-[color:var(--bs-gold)]">Confirmation</div>
        <h3 className="mt-4 font-display text-4xl md:text-5xl font-light">
          Merci, {form.full_name.split(" ")[0]}.
        </h3>
        <p className="mt-4 text-[color:var(--bs-muted)] font-light leading-relaxed max-w-lg">
          Votre demande est arrivée. Nous vous répondons sous 24–48 h à{" "}
          <span className="text-[color:var(--bs-ink)]">{form.email}</span>. À
          très vite au-dessus de la Belgique.
        </p>
        <button
          className="btn-ghost mt-8"
          onClick={() => {
            setDone(false);
            setForm({
              full_name: "",
              email: "",
              phone: "",
              flight_type: EXPERIENCES[0].title,
              helicopter: "",
              duration: "",
              passengers: 1,
              preferred_date: "",
              message: "",
            });
          }}
          data-testid="booking-reset"
        >
          Nouvelle demande
        </button>
      </motion.div>
    );
  }

  const inputCls =
    "w-full bg-transparent border-b border-black/20 focus:border-[color:var(--bs-ink)] py-3 outline-none transition-colors text-[color:var(--bs-ink)] font-light";
  const labelCls = "overline text-[color:var(--bs-muted)]";

  return (
    <form
      onSubmit={submit}
      data-testid="booking-form"
      className={compact ? "" : "border border-black/10 bg-white p-8 md:p-12"}
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10">
        <div>
          <label className={labelCls}>Nom complet *</label>
          <input
            type="text"
            required
            value={form.full_name}
            onChange={upd("full_name")}
            data-testid="booking-name"
            className={inputCls}
            placeholder="Prénom Nom"
          />
        </div>
        <div>
          <label className={labelCls}>Email *</label>
          <input
            type="email"
            required
            value={form.email}
            onChange={upd("email")}
            data-testid="booking-email"
            className={inputCls}
            placeholder="vous@exemple.be"
          />
        </div>
        <div>
          <label className={labelCls}>Téléphone *</label>
          <input
            type="tel"
            required
            value={form.phone}
            onChange={upd("phone")}
            data-testid="booking-phone"
            className={inputCls}
            placeholder="+32 …"
          />
        </div>
        <div>
          <label className={labelCls}>Date souhaitée</label>
          <input
            type="date"
            value={form.preferred_date}
            onChange={upd("preferred_date")}
            data-testid="booking-date"
            className={inputCls}
          />
        </div>
        <div>
          <label className={labelCls}>Type de vol</label>
          <select
            value={form.flight_type}
            onChange={upd("flight_type")}
            data-testid="booking-flighttype"
            className={inputCls}
          >
            {EXPERIENCES.map((e) => (
              <option key={e.id} value={e.title}>
                {e.title}
              </option>
            ))}
            <option value="Autre / sur mesure">Autre / sur mesure</option>
          </select>
        </div>
        <div>
          <label className={labelCls}>Hélicoptère</label>
          <select
            value={form.helicopter}
            onChange={upd("helicopter")}
            data-testid="booking-heli"
            className={inputCls}
          >
            {HELIS.map((h) => (
              <option key={h.label} value={h.v}>
                {h.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className={labelCls}>Durée</label>
          <select
            value={form.duration}
            onChange={upd("duration")}
            data-testid="booking-duration"
            className={inputCls}
          >
            <option value="">— Sélection —</option>
            {DURATIONS.map((d) => (
              <option key={d.v} value={d.v}>
                {d.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className={labelCls}>Nombre de passagers</label>
          <input
            type="number"
            min={1}
            max={6}
            value={form.passengers}
            onChange={upd("passengers")}
            data-testid="booking-pax"
            className={inputCls}
          />
        </div>
      </div>

      <div className="mt-10">
        <label className={labelCls}>Message / précisions</label>
        <textarea
          rows={4}
          value={form.message}
          onChange={upd("message")}
          data-testid="booking-message"
          className={`${inputCls} resize-none`}
          placeholder="Occasion, itinéraire souhaité, poids passagers, questions…"
        />
      </div>

      <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <p className="text-xs text-[color:var(--bs-muted)] font-light max-w-md">
          En envoyant ce formulaire, vous acceptez d'être recontacté(e) par
          BSCOPTER à propos de votre demande.
        </p>
        <button
          type="submit"
          disabled={submitting}
          data-testid="booking-submit"
          className="btn-gold disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {submitting ? "Envoi…" : "Envoyer la demande"}
          <span aria-hidden>→</span>
        </button>
      </div>
    </form>
  );
}
